// ─── Keycloak OIDC Authorization Code + PKCE ─────────────────────────
// Tokens stored IN MEMORY only (not localStorage/sessionStorage)
// code_verifier stored in sessionStorage only during redirect flow

import { runtimeConfig } from "../runtimeConfig";

/** Keycloak auth configuration — injectable via config.js or env vars. */
export interface AuthConfig {
  realmUrl: string;
  clientId: string;
  redirectUri: string;
  scopes: string[];
}

/** Token response from Keycloak token endpoint. */
interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

/** Runtime guard for TokenResponse. */
function isTokenResponse(value: unknown): value is TokenResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.access_token === "string" &&
    typeof obj.id_token === "string" &&
    typeof obj.refresh_token === "string" &&
    typeof obj.expires_in === "number" &&
    typeof obj.token_type === "string"
  );
}

// ─── PKCE helpers ─────────────────────────────────────────────────────

/** Generate a cryptographically random string of given length. */
function generateRandom(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(36).padStart(2, "0"))
    .join("")
    .slice(0, length);
}

/** Compute S256 code_challenge from code_verifier. */
async function computeCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

/** Base64url encode (no padding, URL-safe). */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ─── Default configuration ────────────────────────────────────────────

// v1.10.0 : lue au chargement (config.js), plus aucune adresse écrite en dur.
const DEFAULT_CONFIG: AuthConfig = {
  realmUrl: runtimeConfig.keycloakRealmUrl,
  clientId: runtimeConfig.keycloakClientId,
  redirectUri: runtimeConfig.authRedirectUri,
  scopes: ["openid", "profile", "email"],
};

// ─── Session storage keys (PKCE flow only) ────────────────────────────

const SK_CODE_VERIFIER = "auth_code_verifier";
const SK_STATE = "auth_state";
const SK_LOGOUT_PENDING = "auth_logout_pending";

type NavigateTo = (url: string) => void;

// ─── AuthService ──────────────────────────────────────────────────────

export class AuthService {
  private accessToken: string | null = null;
  private idToken: string | null = null;
  private refreshToken: string | null = null;
  private expiresAt: number = 0;
  private refreshTimerId: ReturnType<typeof setTimeout> | null = null;
  private readonly config: AuthConfig;
  private readonly navigateTo: NavigateTo;

  /** Listeners notified when auth state changes. */
  private listeners: Array<() => void> = [];

  constructor(config?: Partial<AuthConfig>, navigateTo?: NavigateTo) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.navigateTo =
      navigateTo ?? ((url: string) => window.location.assign(url));
  }

  /** Check if user is authenticated (token present + not expired). */
  isAuthenticated(): boolean {
    return this.accessToken !== null && Date.now() < this.expiresAt;
  }

  /** Get current access token (or null if expired/missing). */
  getToken(): string | null {
    if (!this.isAuthenticated()) return null;
    return this.accessToken;
  }

  /** Decode the JWT payload of the current token (null if none/malformed). */
  getClaims(): Record<string, unknown> | null {
    const token = this.getToken();
    if (!token) return null;
    const part = token.split(".")[1];
    if (!part) return null;
    try {
      const padded = part.replace(/-/g, "+").replace(/_/g, "/");
      return JSON.parse(atob(padded)) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  /** Realm roles carried by the current token. */
  getRoles(): string[] {
    const claims = this.getClaims();
    const realm = claims?.["realm_access"] as { roles?: string[] } | undefined;
    return realm?.roles ?? [];
  }

  /** True when the current user carries the `admin` realm role. */
  isAdmin(): boolean {
    return this.getRoles().includes("admin");
  }

  /** True while the browser is completing an explicit Keycloak logout. */
  isLogoutPending(): boolean {
    return sessionStorage.getItem(SK_LOGOUT_PENDING) === "true";
  }

  /** Subscribe to auth state changes. Returns unsubscribe function. */
  subscribe(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  // ── PKCE Login Flow ───────────────────────────────────────────────

  /** Start PKCE login — redirect to Keycloak authorization endpoint. */
  async login(): Promise<void> {
    // A new login must only happen after an explicit user action once logout
    // has started. Clearing the marker here prevents an AuthGuard redirect loop.
    sessionStorage.removeItem(SK_LOGOUT_PENDING);

    const codeVerifier = generateRandom(64);
    const codeChallenge = await computeCodeChallenge(codeVerifier);
    const state = generateRandom(32);

    // Store in sessionStorage — survives redirect, cleaned after callback
    sessionStorage.setItem(SK_CODE_VERIFIER, codeVerifier);
    sessionStorage.setItem(SK_STATE, state);

    const params = new URLSearchParams({
      response_type: "code",
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      scope: this.config.scopes.join(" "),
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
      state,
    });

    this.navigateTo(
      `${this.config.realmUrl}/protocol/openid-connect/auth?${params.toString()}`,
    );
  }

  /** Handle callback from Keycloak — exchange authorization code for tokens. */
  async handleCallback(code: string, state: string): Promise<void> {
    // Verify state matches (CSRF protection)
    const savedState = sessionStorage.getItem(SK_STATE);
    if (state !== savedState) {
      throw new Error("Invalid state parameter — possible CSRF attack");
    }

    // Retrieve code_verifier
    const codeVerifier = sessionStorage.getItem(SK_CODE_VERIFIER);
    if (!codeVerifier) {
      throw new Error("Missing code_verifier — PKCE flow interrupted");
    }

    // Exchange code for tokens
    const body = new URLSearchParams({
      grant_type: "authorization_code",
      code,
      code_verifier: codeVerifier,
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
    });

    const response = await fetch(
      `${this.config.realmUrl}/protocol/openid-connect/token`,
      {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      },
    );

    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    const data: unknown = await response.json();
    if (!isTokenResponse(data)) {
      throw new Error("Invalid token response from Keycloak");
    }

    // Store tokens IN MEMORY only
    this.accessToken = data.access_token;
    this.idToken = data.id_token;
    this.refreshToken = data.refresh_token;
    this.expiresAt = Date.now() + data.expires_in * 1000;

    // Clean up sessionStorage (PKCE artifacts)
    sessionStorage.removeItem(SK_CODE_VERIFIER);
    sessionStorage.removeItem(SK_STATE);
    sessionStorage.removeItem(SK_LOGOUT_PENDING);

    // Schedule automatic refresh
    this.scheduleRefresh(data.expires_in);
    this.notify();
  }

  /** Refresh access token using refresh_token grant. */
  async refreshAccessToken(): Promise<void> {
    if (!this.refreshToken) {
      this.clearTokens();
      return;
    }

    const body = new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: this.refreshToken,
      client_id: this.config.clientId,
    });

    try {
      const response = await fetch(
        `${this.config.realmUrl}/protocol/openid-connect/token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        },
      );

      if (!response.ok) {
        this.clearTokens();
        return;
      }

      const data: unknown = await response.json();
      if (!isTokenResponse(data)) {
        this.clearTokens();
        return;
      }

      this.accessToken = data.access_token;
      this.idToken = data.id_token;
      this.refreshToken = data.refresh_token;
      this.expiresAt = Date.now() + data.expires_in * 1000;

      this.scheduleRefresh(data.expires_in);
      this.notify();
    } catch {
      this.clearTokens();
    }
  }

  /** Logout — clear tokens + redirect to Keycloak logout endpoint. */
  logout(): void {
    const hint = this.idToken;

    // Set this before notifying React that tokens are gone. Without the marker,
    // AuthGuard immediately starts a fresh OIDC login and can win the race with
    // the Keycloak end-session navigation, making Logout appear ineffective.
    sessionStorage.setItem(SK_LOGOUT_PENDING, "true");
    sessionStorage.removeItem(SK_CODE_VERIFIER);
    sessionStorage.removeItem(SK_STATE);
    this.clearTokens();

    const params = new URLSearchParams({
      client_id: this.config.clientId,
      post_logout_redirect_uri: window.location.origin,
    });

    if (hint) {
      params.set("id_token_hint", hint);
    }

    this.navigateTo(
      `${this.config.realmUrl}/protocol/openid-connect/logout?${params.toString()}`,
    );
  }

  /** Clear all tokens and cancel refresh timer. */
  private clearTokens(): void {
    this.accessToken = null;
    this.idToken = null;
    this.refreshToken = null;
    this.expiresAt = 0;
    if (this.refreshTimerId !== null) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
    this.notify();
  }

  /** Schedule automatic token refresh 60 seconds before expiry. */
  private scheduleRefresh(expiresIn: number): void {
    if (this.refreshTimerId !== null) {
      clearTimeout(this.refreshTimerId);
    }

    // Refresh at 75% of TTL (handles short-lived tokens like 60s), minimum 5s
    const delayMs = Math.max(expiresIn * 750, 5000);

    this.refreshTimerId = setTimeout(() => {
      this.refreshAccessToken().catch(() => {
        this.clearTokens();
      });
    }, delayMs);
  }
}

// ─── Singleton instance ───────────────────────────────────────────────

export const authService = new AuthService();
